import React from "react";
import { getSessionDetails } from "../utils/userSession";
import moment from "moment";
import { UserSessionDialogProps, getContextRows } from "./user_session/UserSessionDialog";
import TitledDialog from "./TitledDialog";
import { Button, Typography, Paper } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { styled } from "@mui/material/styles";
import { FormattedMessage } from "react-intl";
import { labelTranslations } from "./user_session/common";

// Styled components for the grid
const LabelGrid = styled(Grid)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1.5),
    fontWeight: "bold",
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ValueGrid = styled(Grid)(({ theme }) => ({
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

// Default props for the grid components
LabelGrid.defaultProps = {
    size: { xs: 12, sm: 4 },
};

ValueGrid.defaultProps = {
    size: { xs: 12, sm: 8 },
};

const SessionDetailsContainer = styled(Paper)(({ theme }) => ({
    marginTop: theme.spacing(2),
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
}));

const Unknown = () => <FormattedMessage {...labelTranslations.unknown} />;

const UserServiceSessionDialog = ({
    onHide,
    onExited,
    onDelete,
    session,
    show,
    disableDeleteCurrentSession,
}: UserSessionDialogProps) => {
    const getAppName = () => {
        return session["apps"][0]["name"];
    };

    const getBody = () => {
        const { lastUse, location } = getSessionDetails(session);

        const userAgent = session["user_agent"];

        const osVersion = userAgent && userAgent["os"]["version"].length > 0 ? ` (${userAgent["os"]["version"]})` : "";

        return (
            <SessionDetailsContainer elevation={0}>
                <Grid container>
                    <LabelGrid><FormattedMessage {...labelTranslations.os} /></LabelGrid>
                    <ValueGrid>{userAgent ? `${userAgent["os"]["name"]}${osVersion}` : <Unknown />}</ValueGrid>

                    <LabelGrid><FormattedMessage {...labelTranslations.client} /></LabelGrid>
                    <ValueGrid>
                        {userAgent ? `${userAgent["client"]["name"]} (${userAgent["client"]["version"]})` : <Unknown />}
                    </ValueGrid>

                    <LabelGrid><FormattedMessage {...labelTranslations.ip} /></LabelGrid>
                    <ValueGrid>{session["ip"] || <Unknown />}</ValueGrid>

                    <LabelGrid><FormattedMessage {...labelTranslations.location} /></LabelGrid>
                    <ValueGrid>{location}</ValueGrid>

                    <LabelGrid><FormattedMessage {...labelTranslations.createdService} /></LabelGrid>
                    <ValueGrid>
                        {moment(session["created"]).format("LLL")}
                        {` (${moment
                            .duration(moment.duration(moment(session["created"]).diff(moment())))
                            .humanize(true)})`}
                    </ValueGrid>

                    <LabelGrid><FormattedMessage {...labelTranslations.last_use} /></LabelGrid>
                    <ValueGrid>
                        {session["last_use"] ? (
                            <span>
                                {moment(session["last_use"]).format("LLL")} ({lastUse})
                            </span>
                        ) : (
                            <FormattedMessage {...labelTranslations.never} />
                        )}
                    </ValueGrid>

                    <LabelGrid><FormattedMessage {...labelTranslations.app_identifier} /></LabelGrid>
                    <ValueGrid>{session["apps"][0]["identifier"]}</ValueGrid>

                    {getContextRows(session)}
                </Grid>
            </SessionDetailsContainer>
        );
    };

    return (
        <TitledDialog
            open={show}
            isContentChanged={() => false}
            hide={onHide}
            onExited={onExited}
            title={
                <>
                    <FormattedMessage {...labelTranslations.dialog_title_service} />
                    <Typography component="span" style={{ paddingLeft: 5 }}>
                        {getAppName()}
                    </Typography>
                </>
            }
            actions={
                session["current"] && disableDeleteCurrentSession ? null : (
                    <Button onClick={onDelete} color="error">
                        <FormattedMessage {...labelTranslations.delete} />
                    </Button>
                )
            }
        >
            {getBody()}
        </TitledDialog>
    );
};

export default UserServiceSessionDialog;
